import React, { useEffect, useState } from 'react';
import apiUrls from '../../api/apiUrls';
import fetchWithAuth from '../../api/fetchWithAuth';
import { useLang } from "../../contexts/LanguageContext";
import { Modal, Button } from 'react-bootstrap';
import './LemmaModal.css';

const LemmaModal = ({ show, handleClose, onModalError, langOrigen, dictLanguage, lemmaId, lemmaInfor }) => {
  const { t } = useLang();

  const [lemmaData, setLemmaData] = useState(null);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const formatError = (err) => {
    if (!err) return null;
    if (typeof err === 'string') return err;
    if (typeof err === 'object') {
      if (err.message) return err.message;
      if (err.error) return formatError(err.error);
      try { return JSON.stringify(err); } catch { return String(err); }
    }
    return String(err);
  };

  useEffect(() => {
    const shouldFetch = show && (lemmaId !== null && lemmaId !== undefined && lemmaId !== '');
    const controller = new AbortController();
    const signal = controller.signal;

    if (!shouldFetch) {
      setLemmaData(null);
      setError(null);
      setIsLoading(false);
      return () => controller.abort();
    }

    const fetchLemmaData = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const token = localStorage.getItem('token');
        
        // --- TRIAL LOGIC FOR GUESTS ---
        if (!token) {
          const trialCount = parseInt(sessionStorage.getItem('dict_trial_count') || '0');
          if (trialCount >= 3) {
            if (onModalError) onModalError("PAYWALL");
            setError("PAYWALL"); 
            setIsLoading(false);
            return;
          }
          // Incrementar contador
          sessionStorage.setItem('dict_trial_count', (trialCount + 1).toString());
        }
        // ------------------------------

        const finalDict = `${langOrigen}_${dictLanguage || langOrigen}`;
        const url = `${apiUrls.apiUrlLemma}${finalDict}/${lemmaId}`;
        
        let response;
        if (token) {
          response = await fetchWithAuth(url, { signal });
        } else {
          response = await fetch(url, { signal });
        }

        //console.log("URL:" + url);

        if (!response.ok) {
          if (response.status === 403) {
            try {
              const errData = await response.json();
              const errCode = errData?.error || "PAYWALL";
              if (onModalError) onModalError(errCode);
              setError(errCode);
              setIsLoading(false);
              return;
            } catch (e) {
              // Ignore parse error and fallback
            }
            if (onModalError) onModalError("PAYWALL");
            setError("PAYWALL");
            setIsLoading(false);
            return;
          }

          // 404 -> entrada no encontrada; otros -> error de API
          if (response.status === 404) {
            setError(t('LEMMA_MODAL_NOT_FOUND'));
          } else {
            setError(`${t('LEMMA_MODAL_API_ERROR')} (${response.status})`);
          }
          setLemmaData(null);
          return;
        }

        const data = await response.json();

        if (data?.error) {
          // Normalizar si la API devuelve "LEMMA_NOT_FOUND" (o viene dentro de un objeto)
          const apiErrStr = formatError(data.error);
          if (apiErrStr === 'LEMMA_NOT_FOUND') {
            setError(t('LEMMA_MODAL_NOT_FOUND'));
          } else {
            setError(apiErrStr || t('LEMMA_MODAL_API_ERROR'));
          }
          setLemmaData(null);
          return;
        }

        const payload = data?.data ?? data;
        // payload vacío -> no encontrada
        const isEmpty = !payload || (typeof payload === 'object' && Object.keys(payload).length === 0);
        if (isEmpty) {
          setError(t('LEMMA_MODAL_NOT_FOUND'));
          setLemmaData(null);
        } else {
          setLemmaData(payload);
          setError(null);
          //console.log('Datos del lema obtenidos:', payload);
        }
      } catch (err) {
        if (err.name === 'AbortError') return;
        console.error('Error al consultar la API:', err);
        // si el error contiene "LEMMA_NOT_FOUND", usar la traducción; si no, mostrar texto o traducción genérica
        const errStr = formatError(err);
        setError(errStr === 'LEMMA_NOT_FOUND' ? t('LEMMA_MODAL_NOT_FOUND') : (errStr || t('LEMMA_MODAL_NETWORK_ERROR')));
        setLemmaData(null);
      } finally {
        setIsLoading(false);
      }
    };

    fetchLemmaData();

    return () => controller.abort();
  }, [show, lemmaId, langOrigen, dictLanguage, t]);

  const closeAndCleanup = (resumeAudio = false) => {
    handleClose(resumeAudio);
    setLemmaData(null);
    setError(null);
  };

  const handleModalHide = () => closeAndCleanup(false);
  const handleContinueClick = () => closeAndCleanup(true);

  // Render helpers
  

  const renderPhonetics = (phon) => {
    if (!phon) return null;
    if (Array.isArray(phon)) {
      return phon.flatMap((item, index) => {
        // Caso 1: item con forma { 'es-ES': '...', 'es-419': '...' }
        if (item && typeof item === 'object') {
          return Object.entries(item).map(([dialCode, phonetic], i) => (
            <div  key={`p-o-${index}-${i}`} className="phon-block">
              <span className='phon'>/{phonetic}/ </span>
              <span className='phon-dialect'>{t(dialCode)}</span>
            </div>
          ));
        }
        // Caso 2: item string simple
        return (
          <span key={`p-s-${index}`} className="dial">/{String(item)}/</span>
        );
      });
    }
    else {
      return <span className="dial">/{String(phon)}/</span>;
    }
  };

  const parseJsonData = () => {
    const value = lemmaData?.json_data;
    if (!value) return null;
    if (typeof value === 'string') {
      try {
        return JSON.parse(value);
      } catch {
        return null;
      }
    }
    return value;
  };

  const renderDefinitionsFromJsonData = () => {
    const jsonData = parseJsonData();
    if (!Array.isArray(jsonData)) return null;

    const blocks = [];
    jsonData.forEach((entry, entryIdx) => {
      if (!entry || typeof entry !== 'object') return;
      Object.entries(entry).forEach(([lemmaKey, lemmaObj], kIdx) => {
        const etys = Array.isArray(lemmaObj?.etymologies) ? lemmaObj.etymologies : [];
        etys.forEach((ety, eIdx) => {
          if (ety?.etymology) {
            blocks.push(
              <div className="etymology" key={`ety-${entryIdx}-${kIdx}-${eIdx}`}>
                ⇒ {ety.etymology}                
              </div>
            );
          }

          const defs = Array.isArray(ety?.definitions) ? ety.definitions : [];
          if (defs.length > 0) {
            blocks.push(
              <div className="definition-block" key={`defs-${lemmaKey}-${entryIdx}-${kIdx}-${eIdx}`}>
                <ol className="number-first-level">
                  {defs.map((def, dIdx) => (
                    <li key={def.number ?? `def-${entryIdx}-${kIdx}-${eIdx}-${dIdx}`}>
                      {def.partOfSpeech && <span className="pos">{def.partOfSpeech}&nbsp;</span>}
                      {def.meaning && <span className="meaning-first-level">{def.meaning}</span>}
                      {def.example && <p className="exa">{def.example}</p>}
                      {Array.isArray(def.subdefinitions) && def.subdefinitions.length > 0 && (
                        <ol className="number-second-level">
                          {def.subdefinitions.map((sd, sIdx) => (
                            <li key={sd.number ?? `sub-${entryIdx}-${kIdx}-${eIdx}-${dIdx}-${sIdx}`}>
                              {sd.meaning && <span className="meaning-second-level">{sd.meaning}</span>}
                              {sd.example && <p className="exa">{sd.example}</p>}
                            </li>
                          ))}
                        </ol>
                      )}
                    </li>
                  ))}
                </ol>
              </div>
            );
          }
        });
      });
    });

    if (blocks.length === 0) return null;
    return <div className="definitions">{blocks}</div>;
  };

  return (
    <Modal show={show} onHide={handleModalHide}>
      {/* Modal con botón de cerrar blando */}
      <Modal.Header closeButton title={t("LEMMA_MODAL_JUST_CLOSE")} data-bs-theme="dark" className="bg-dark">
        <Modal.Title>
          {lemmaData?.word || (isLoading ? t("LEMMA_MODAL_LOADING") : t("LEMMA_MODAL_NO_DATA"))}
          {/* Si el idioma de origen y el idioma son iguales, mostrar sólo el de origen, de lo contrario mostrar ambos */}
          
          <div className='dict-selected'>
            {langOrigen} → {dictLanguage}
          </div>

        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {/* Nota: Eliminar logs excesivos en producción */}
        {error && <p className="text-danger">{error}</p>}
        {!error && !lemmaData && !isLoading && <p>{t('LEMMA_MODAL_NOT_FOUND')}</p>}
        {lemmaData && (
          <>
            <div className="phon">
              {lemmaData.dial && <span className="dial">{lemmaData.dial}</span>}
              {renderPhonetics(lemmaData.phon)}
            </div>
            {renderDefinitionsFromJsonData() || (
              <div className="definitions">
                {Array.isArray(lemmaData.definitions) && (
                  <ol className="meaning-first-level">
                    {lemmaData.definitions.map((def, index) => (
                      <li key={index}>
                        {def.partOfSpeech && <p className="pos">{def.partOfSpeech}</p>}
                        {def.meaning && <p>{def.meaning}</p>}
                        {def.example && <p className="exa">{def.example}</p>}
                      </li>
                    ))}
                  </ol>
                )}
              </div>
            )}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleContinueClick}>
          {t("LEMMA_MODAL_CLOSE")}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
export default LemmaModal;
